import { DonationProvider } from '@joindeed/calculate-fees';
import { Map, OrderedMap, OrderedSet } from 'immutable';
import { Observable } from 'rxjs';

import { showErrorAction } from 'containers/Notification/actions';

import Api from '../api';

import Donation from './model';
import {
  setTotalAction,
  addAction,
  addMultipleAction,
  updateMultipleAction,
  setDonationsLoaded,
  setDisplayedDonationIdsAction,
} from './actions';

export interface DonationProviderData {
  donateViaNonprofits: Array<{
    id: string;
    name: string;
    routed: boolean;
    childname: string;
    payeeId: string;
  }>;
  donationProvider: DonationProvider;
}

export default class DonationsApi {
  static fetch(query = {}) {
    const queryStr = Object.keys(query)
      .map((key) => query[key] && `${key}=${query[key]}`)
      .filter(Boolean)
      .join('&');
    return Api.get(`api/donations?${queryStr}`).map((result) =>
      [
        addMultipleAction(
          OrderedMap(
            result.donations &&
              result.donations.map((donation) => [donation.id, new Donation(donation)])
          )
        ),
        setDisplayedDonationIdsAction(OrderedSet(result?.donations.map((donation) => donation.id))),
        'total' in result && setTotalAction(result.total),
      ].filter(Boolean)
    );
  }

  static fetchExternal(passedQuery = {}) {
    const query = {
      external: 1,
      ...passedQuery,
    };
    const queryStr = Object.keys(query)
      .map((key) => query[key] && `${key}=${query[key]}`)
      .filter(Boolean)
      .join('&');

    return Api.get(`api/donations?${queryStr}`);
  }

  static fetchById(id: string) {
    return Api.get(`api/donations/${id}`).map((donation) => addAction(new Donation(donation)));
  }

  static fetchDonations(userId: string) {
    return Api.get(`api/donations/users/${userId}`).map((donations) => [
      addMultipleAction(Map(donations.map((donation) => [donation.id, new Donation(donation)]))),
      setDonationsLoaded(userId),
    ]);
  }

  static matchDonations(donationIds: string[]) {
    return Api.post('api/donations/match', {
      donationIds,
    }).map(({ matched, failed }) => [
      updateMultipleAction(Map(matched.map((donation) => [donation.id, new Map(donation)]))),
      ...failed.map((donation) => showErrorAction(donation.matchingError)),
    ]);
  }

  static unmatchDonations(donationIds: string[]) {
    return Api.post('api/donations/unmatch', {
      donationIds,
    }).map((donations) => [
      updateMultipleAction(Map(donations.map((donation) => [donation.id, new Map(donation)]))),
    ]);
  }

  static approveOrDenyExternalDonations(
    donationIds: string[],
    approve = true,
    rejectionReason?: string
  ) {
    return Api.post(`api/donations/handleExternalDonations/${approve ? 'approve' : 'deny'}`, {
      donationIds,
      rejectionReason,
    }).map((donations) =>
      updateMultipleAction(Map(donations.map((donation) => [donation.id, new Map(donation)])))
    );
  }

  static logCorporateDonation(data: any) {
    return Api.post(`api/donations/corporate`, data).map((donation) =>
      addAction(new Donation(donation))
    );
  }

  static cancelCorporateDonation(corporateDonationId: string) {
    return Api.delete(`api/donations/corporate/${corporateDonationId}`);
  }

  static fetchDonationProviderDataForNonprofit(
    nonprofitId: string
  ): Observable<DonationProviderData> {
    return Api.get(`api/donations/nonprofit/${nonprofitId}/provider`);
  }
}
